import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    forkJoin,
    finalize,
    Observable,
    of,
    ReplaySubject,
    shareReplay,
    Subject,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { UserAuth, UserProfile } from '../profile/models/user-profile';
import { UserService } from './user.service';
import { IdmOption } from '../profile/models/idm-option';
import { LoginRedirectionService } from './login-redirection.service';
import { CreditsService } from 'src/app/shared/services/credits.service';
import { SubscriptionDto } from 'src/app/shared/models/credit-service-dtos';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private isLoggedInSubject$: BehaviorSubject<boolean> = new BehaviorSubject(
        false
    );
    public logoutPreparationsFinished$: Subject<boolean> = new Subject();

    private currentUser$: BehaviorSubject<UserAuth> = new BehaviorSubject(
        {} as UserAuth
    );
    private currentUserSubscription$?: Observable<SubscriptionDto | null>;
    private refreshInProgress$?: Observable<UserAuth>;
    private activeContextChangedSubject$ = new Subject<UserAuth>();
    public isLoggedIn$: Observable<boolean> =
        this.isLoggedInSubject$.asObservable();
    public logoutTriggered$ = new Subject();
    public initializationFinished$ = new ReplaySubject<boolean>();
    public activeContextChanged$: Observable<UserAuth> =
        this.activeContextChangedSubject$.asObservable();
    public firstInitialization = true;

    constructor(
        private userService: UserService,
        private redirectionService: LoginRedirectionService,
        private creditsService: CreditsService
    ) {
        forkJoin([userService.getMe(), userService.getIdmOption()])
            .pipe(
                switchMap(([userAuth, idmOption]: [UserAuth, IdmOption]) => {
                    this.currentUser$ = new BehaviorSubject(userAuth);
                    redirectionService.setIdmOptions(idmOption);
                    this.isLoggedInSubject$.next(!!userAuth.id);

                    if (this.isLoggedIn()) {
                        userService.getProfilePicture(userAuth.id);
                    }
                    let expectedActiveOrg: string[] = [];
                    Object.keys(userAuth.organizationProperties).forEach(
                        (orgId) => {
                            if (userAuth.organizationProperties[orgId].orgBound)
                                expectedActiveOrg.push(orgId);
                        }
                    );
                    if (
                        expectedActiveOrg.length > 0 &&
                        !expectedActiveOrg.includes(userAuth.activeOrganization)
                    ) {
                        return this.userService
                            .updateUser(
                                {
                                    activeOrganization: expectedActiveOrg[0],
                                    activeOrganizationGroup: '',
                                } as UserProfile,
                                userAuth.id
                            )
                            .pipe(
                                switchMap(() => this.refreshUserAuth(false)),
                                tap(() => {
                                    this.firstInitialization = false;
                                })
                            );
                    } else if (this.firstInitialization) {
                        return this.refreshUserAuth(false).pipe(
                            tap(() => {
                                this.firstInitialization = false;
                            })
                        );
                    }
                    return of(null);
                })
            )
            .subscribe({
                next: () => {
                    this.initializationFinished$.next(true);
                },
                error: () => {
                    this.currentUser$.next({} as UserAuth);
                    this.isLoggedInSubject$.next(false);
                    this.initializationFinished$.next(true);
                },
            });
    }

    signOut(): void {
        if (this.logoutTriggered$.observed) {
            this.logoutPreparationsFinished$.pipe(take(1)).subscribe(() => {
                this.sendLogoutRequest();
            });
        }
        this.logoutTriggered$.next(true);
        this.currentUser$.next({} as UserAuth);
        this.resetCurrentUserSubscriptionCache();
        this.isLoggedInSubject$.next(false);
        if (!this.logoutTriggered$.observed) {
            this.sendLogoutRequest();
        }
        this.userService.deleteProfilePicture();
    }

    private sendLogoutRequest() {
        this.userService.logoutFromIdm().subscribe({
            next: (resp) => {
                const logoutUri = resp.headers.get('Location');
                if (!!logoutUri) {
                    this.redirect(logoutUri);
                } else {
                    this.redirect('/');
                }
            },
            error: () => {
                this.redirect('/');
            },
        });
    }
    private redirect(url: string) {
        window.location.href = url;
    }

    public observableUserAuth(): Observable<UserAuth> {
        return this.currentUser$.asObservable();
    }

    public saveUser(user: UserAuth): void {
        this.updateCurrentUser(user);
        this.isLoggedInSubject$.next(!!user.id);

        if (!!user.id) {
            this.userService.getProfilePicture(user.id);
        }
    }

    public get currentUserValue(): UserAuth {
        return this.currentUser$?.value;
    }

    public getCurrentUserSubscription(): Observable<SubscriptionDto | null> {
        if (this.currentUserSubscription$) {
            return this.currentUserSubscription$;
        }

        const userId = this.currentUserValue?.id;
        if (!userId) {
            return of(null);
        }

        this.currentUserSubscription$ = this.creditsService
            .getCurrentUserSubscription(userId)
            .pipe(
                catchError(() => of(null)),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        return this.currentUserSubscription$;
    }

    private resetCurrentUserSubscriptionCache(): void {
        this.currentUserSubscription$ = undefined;
    }

    public isLoggedIn(): boolean {
        return this.isLoggedInSubject$.value;
    }

    public isAdmin(): boolean {
        return !!this.currentUserValue.roles.includes('ROLE_CONFIG_ADMIN');
    }

    public isExperimental(): boolean {
        return !!this.currentUserValue.roles.includes('ROLE_USER_EXPERIMENTAL');
    }

    public isUserApiTokenCreator(): boolean {
        return !!this.currentUserValue.roles.includes(
            'ROLE_USER_API_TOKEN_CREATOR'
        );
    }
    public isCreditsAdmin(): boolean {
        return !!this.currentUserValue.roles.includes('ROLE_CREDIT_ADMIN');
    }

    public hasMotoricTrainingRights(): boolean {
        return !!this.currentUserValue.roles.includes(
            'ROLE_USER_MOTORIC_TRAINING'
        );
    }

    public hasVisualTrainingRights(): boolean {
        return !!this.currentUserValue.roles.includes(
            'ROLE_USER_VISUAL_TRAINING'
        );
    }

    public hasOrganizationFeatureAccess(): boolean {
        return !!this.currentUserValue.roles.includes(
            'ROLE_ORGANIZATION_FEATURE'
        );
    }

    public isUserBoundToAnyOrg(): boolean {
        const organizations = Object.values(
            this.currentUserValue.organizationProperties
        );
        let orgBound = false;
        organizations.forEach((organization) => {
            if (organization.orgBound) {
                orgBound = true;
            }
        });
        return orgBound;
    }

    public isAtLeastOrganizationAdmin(organizationId: string): boolean {
        return (
            this.isOrgSuperAdmin(organizationId) ||
            this.isOrgAdmin(organizationId)
        );
    }

    public isOrganizationSuperAdmin(organizationId: string): boolean {
        return this.isOrgSuperAdmin(organizationId);
    }

    public refreshUserAuth(emitContextChange = true): Observable<UserAuth> {
        if (!this.refreshInProgress$) {
            this.refreshInProgress$ = this.userService.refreshJwtToken().pipe(
                switchMap(() =>
                    this.userService.getMe({
                        bypassSessionRefreshInterceptor: true,
                    })
                ),
                finalize(() => {
                    this.refreshInProgress$ = undefined;
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }

        return this.refreshInProgress$.pipe(
            tap((userAuth) => {
                this.publishRefreshedUserAuth(userAuth, emitContextChange);
            })
        );
    }

    private publishRefreshedUserAuth(
        userAuth: UserAuth,
        emitContextChange = true
    ): void {
        const previousContext = this.normalizeActiveOrganization(
            this.currentUser$?.value?.activeOrganization
        );
        const nextContext = this.normalizeActiveOrganization(
            userAuth?.activeOrganization
        );

        this.updateCurrentUser(userAuth);

        if (emitContextChange && previousContext !== nextContext) {
            this.activeContextChangedSubject$.next(userAuth);
        }
    }

    private updateCurrentUser(userAuth: UserAuth): void {
        this.currentUser$.next(userAuth);
        this.resetCurrentUserSubscriptionCache();
    }

    private normalizeActiveOrganization(
        activeOrganization?: string | null
    ): string {
        return activeOrganization || '';
    }

    private isOrgSuperAdmin(organizationId: string) {
        return this.currentUserValue.roles.includes(
            organizationId + '|ADMINISTRATION|SUPERADMIN'
        );
    }
    private isOrgAdmin(organizationId: string) {
        return this.currentUserValue.roles.includes(
            organizationId + '|ADMINISTRATION|ADMIN'
        );
    }
}
